import { useCallback, useEffect, useMemo, useState } from "react";
import { api, STATUS_LABEL, type Transaction, type TxPage } from "../api";
import { LateDays, SituationLegend, SituationMark, ClipMark, SeriesMark, type Sit } from "../components/Situation";
import { TxActions } from "../components/TxActions";
import { Btn, Notice, PageHead, ScrollSentinel } from "../components/ui";
import { useEntities } from "../entity";
import { fmtDay, formatMoney, todayISO, type Currency } from "../lib/money";
import { useMe } from "../session";
import { useTxFlow } from "../txflow";
import { usePeriod } from "../period";

type Tab = "planejado" | "cancelado";
const CHUNK = 200;   // máximo por consulta no servidor
type KindFilter = "todos" | "despesa" | "receita";
interface Money { cents: number; decimal: string }
interface PaySummary {
  accounts: { accountId: string; name: string; currency: Currency; status: string; pagar: Money; receber: Money; count: number }[];
  totals: { currency: Currency; pagar: Money; receber: Money; resultado: Money }[];
}

/** Contas a Pagar e Receber: lançamentos planejados (com atraso destacado) e cancelados (restauráveis). */
export function PayReceive() {
  const { me } = useMe();
  const realm = me.user.realm;
  const { current, loading } = useEntities();
  const flow = useTxFlow();
  const [tab, setTab] = useState<Tab>("planejado");
  const [kind, setKind] = useState<KindFilter>("todos");
  const [page, setPage] = useState<TxPage | null>(null);
  const [sum, setSum] = useState<PaySummary | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [chunks, setChunks] = useState(1);   // rolagem contínua: cada vez que se chega perto do fim, mais um bloco de CHUNK lançamentos
  const per = usePeriod();
  const [scope, setScope] = useState<"mes" | "todos">("mes");
  const [excluded, setExcluded] = useState<string[]>([]); // contas desmarcadas em "Por conta" (todas começam marcadas)
  const canWrite = current?.permission === "total";
  const today = todayISO();

  const load = useCallback(async () => {
    if (!current) return;
    try {
      const k = kind === "todos" ? "" : `&kind=${kind}`;
      const range = scope === "mes" ? `&from=${per.from}&to=${per.to}` : "";
      const sm = await api<PaySummary>("GET", `/api/${realm}/payables-summary?entityId=${current.id}${range}`);
      setSum(sm);
      const included = sm.accounts.map((a) => a.accountId).filter((id) => !excluded.includes(id));
      if (excluded.length && included.length === 0) { setPage({ items: [], total: 0, limit: 50, offset: 0 } as unknown as TxPage); setState("ok"); return; }   // nenhuma conta marcada: nada a mostrar
      const acc = excluded.length ? `&accountIds=${included.join(",")}` : "";
      const base = `/api/${realm}/transactions?entityId=${current.id}&status=${tab}&order=${tab === "planejado" ? "due" : "recent"}&limit=${CHUNK}${k}${range}${acc}`;
      let acc2: TxPage | null = null;
      for (let i = 0; i < chunks; i++) {   // recarrega do começo até o bloco atual (mantém a lista coerente após qualquer alteração)
        const r = await api<TxPage>("GET", `${base}&offset=${i * CHUNK}`);
        acc2 = acc2 ? { ...r, items: [...acc2.items, ...r.items], offset: 0 } : r;
        if (acc2.items.length >= r.total) break;
      }
      setPage(acc2);
      setState("ok");
    } catch { setState("error"); }
  }, [realm, current, tab, kind, chunks, scope, per.from, per.to, excluded]);
  useEffect(() => { setState("loading"); void load(); }, [load, flow.version]);
  useEffect(() => { setChunks(1); }, [tab, kind, scope, per.key, excluded]);

  const totals = useMemo(() => {
    const by = new Map<Currency, { pagar: number; receber: number }>();
    for (const a of sum?.accounts ?? []) {
      if (excluded.includes(a.accountId)) continue;
      const e = by.get(a.currency) ?? { pagar: 0, receber: 0 };
      e.pagar += a.pagar.cents; e.receber += a.receber.cents; by.set(a.currency, e);
    }
    return [...by];
  }, [sum, excluded]);
  const dec = (cents: number) => (cents / 100).toFixed(2);
  const toggle = (id: string) => setExcluded((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  if (loading) return <div className="muted">Carregando…</div>;
  if (!current) return <Notice kind="error">Nenhuma entidade disponível para o seu usuário.</Notice>;

  const sitOf = (t: Transaction): Sit => (t.status === "cancelado" ? "cancelado" : t.status === "planejado" ? (t.overdue ? "vencido" : "agendado") : "realizado");

  return (
    <>
      <PageHead title="Contas a Pagar e Receber" sub={`${current.name} — o que ainda vai acontecer. Ao pagar ou receber, o lançamento passa a contar no saldo atual.`} />
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <div className="toolbar">
        <div className="seg" role="group" aria-label="Situação">
          {([["planejado", "Planejados"], ["cancelado", "Cancelados"]] as const).map(([k, l]) => <button key={k} aria-pressed={tab === k} onClick={() => setTab(k)} data-tab={k}>{l}</button>)}
        </div>
        <div className="seg" role="group" aria-label="Tipo">
          {([["todos", "Todos"], ["despesa", "A pagar"], ["receita", "A receber"]] as const).map(([k, l]) => <button key={k} aria-pressed={kind === k} onClick={() => setKind(k)}>{l}</button>)}
        </div>
        <div className="seg" role="group" aria-label="Período">
          {([["mes", "Mês selecionado"], ["todos", "Todos os períodos"]] as const).map(([k, l]) => <button key={k} aria-pressed={scope === k} onClick={() => setScope(k)} data-scope={k}>{l}</button>)}
        </div>
        <span className="spacer" />
      </div>
      {state === "error" && <Notice kind="error">Não foi possível carregar os lançamentos. Tente novamente.</Notice>}
      <div className="mv-layout">
        <div>
          {totals.map(([cur, v]) => (
            <div className="card mv-summary" key={cur} data-pay-summary={cur}>
              <h3>Resultado do período{totals.length > 1 ? ` — ${cur}` : ""}</h3>
              <table aria-label="Resultado do período">
                <tbody>
                  <tr><td>A pagar</td><td className="tabular neg">− {formatMoney(dec(v.pagar), cur)}</td></tr>
                  <tr><td>A receber</td><td className="tabular pos">+ {formatMoney(dec(v.receber), cur)}</td></tr>
                  <tr className="total"><td>Resultado</td><td className="tabular">{formatMoney(dec(v.receber - v.pagar), cur)}</td></tr>
                </tbody>
              </table>
            </div>
          ))}
          {sum && sum.accounts.length > 0 && (
            <div className="card mv-summary" data-pay-accounts>
              <h3>Por conta {excluded.length > 0 && <button className="link" onClick={() => setExcluded([])} data-pay-all>marcar todas</button>}</h3>
              <table aria-label="Totais por conta">
                <thead><tr><th /><th className="l">Conta</th><th className="num">A pagar</th><th className="num">A receber</th></tr></thead>
                <tbody>
                  {sum.accounts.map((a) => (
                    <tr key={a.accountId}>
                      <td><input type="checkbox" checked={!excluded.includes(a.accountId)} onChange={() => toggle(a.accountId)} aria-label={`Mostrar ${a.name}`} /></td>
                      <td className="l">{a.name}</td>
                      <td className="num tabular neg nowrap">{formatMoney(dec(a.pagar.cents), a.currency)}</td>
                      <td className="num tabular pos nowrap">{formatMoney(dec(a.receber.cents), a.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div>
          {page && page.items.length === 0 && state === "ok" && (
            <div className="card stub"><h3>{tab === "planejado" ? "Nada planejado" : "Nenhum lançamento cancelado"}</h3>
              <p className="muted" style={{ margin: 0 }}>{tab === "planejado" ? "Planeje contas a pagar e a receber para enxergar o saldo previsto." : "Lançamentos cancelados ficam aqui e podem ser restaurados como planejados."}</p></div>
          )}
          {page && page.items.length > 0 && (
            <div className="card table-card">
              <SituationLegend only={tab === "planejado" ? ["vencido", "agendado"] : ["cancelado"]} />
              <table aria-label="Lançamentos" data-planned>
                <thead><tr><th className="sit-col" aria-label="Situação" /><th>Vencimento</th><th>Descrição</th><th>Conta</th><th>Categoria</th><th className="num">Valor</th><th aria-label="Ações" /></tr></thead>
                <tbody>
                  {page.items.map((t: Transaction) => (
                    <tr key={t.id} data-tx={t.description} className={t.status === "cancelado" ? "inactive" : ""}>
                      <td className="sit-col"><SituationMark sit={sitOf(t)} /></td>
                      <td className="tabular">{fmtDay(t.plannedDate)}{t.overdue && <LateDays planned={t.plannedDate} today={today} />}{t.status === "cancelado" && <div className="small muted">{STATUS_LABEL[t.status]}{t.cancelReason ? `: ${t.cancelReason}` : ""}</div>}</td>
                      <td><strong>{t.description}</strong><SeriesMark kind={t.seriesKind} no={t.installmentNo} total={t.installmentTotal} /><ClipMark count={t.attachmentCount} ownerType="transaction" ownerId={t.id} />{t.counterpartyName && <div className="muted small">{t.counterpartyName}</div>}</td>
                      <td>{t.accountName}</td>
                      <td className="small">{t.categoryPath}</td>
                      <td className={`num tabular nowrap ${t.kind === "receita" ? "pos" : "neg"}`}>{t.kind === "receita" ? "+" : "−"} {formatMoney(t.amount.decimal, t.currency)}</td>
                      <td><TxActions tx={t} canWrite={canWrite} onChanged={() => void load()} onMessage={setMsg} menu /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ScrollSentinel watch={page.items.length} onReach={() => setChunks((c) => (page.items.length < page.total && state !== "loading" && page.items.length >= c * CHUNK ? c + 1 : c))} disabled={page.items.length >= page.total} />
              <div className="pager small muted">
                {page.items.length < page.total ? `${page.items.length} de ${page.total} lançamentos` : `${page.total} ${page.total === 1 ? "lançamento" : "lançamentos"}`}
                <span className="spacer" />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
